/**
 * Two apps, one bundle, decided by the path.
 *
 * `/me` is the child's own page. It holds a device token rather than a Cognito
 * session, talks to a different set of routes, and cannot construct a request
 * for anybody else's data. Everything else is the parent app.
 *
 * Split at the top rather than by a route inside a shared shell, because the
 * two surfaces should not share state, a client or a storage key -- and a
 * split here is one somebody can see.
 *
 * `/join` is the third path and it is not a third app. It is the parent app,
 * entered by a link carrying a family code in the fragment, so all it needs
 * is for the code to be taken out of the address bar and put somewhere that
 * survives the sign-in round trip.
 *
 * `/me#code=` is the same idea on the child's side: a link a parent texted to
 * a tablet, carrying that child's own eight-character code. Both are read and
 * cleared BEFORE React renders, below, so no component ever sees a URL with a
 * secret in it and neither code reaches history or a bookmark.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { ChildApp } from './child/ChildApp';
import { captureJoinCode } from './family/join';
import { captureChildCode } from './child/arrive';
// The parental-consent link from the email: held across sign-in, read on Home.
import { takeConsentLink } from './family/consent';
import { appHome, appSide, nativeShell, rememberAppHome } from './native/platform';
import './styles/tokens.css';
import './styles/app.css';
import './styles/living-valley.css';

const root = document.getElementById('root');
if (!root) throw new Error('No #root element.');

// THE OLD ADDRESS IS RETIRED (2026-10-04). The same bundle is still served at
// CloudFront's own name, and anybody arriving there -- a child's tablet, the
// wall display, a bookmark, a link with a code in its fragment -- goes to the
// same path on birbly.app, fragment and all, before anything else runs. The
// API no longer answers the old address, so there is nothing to stay for.
if (window.location.hostname.endsWith('.cloudfront.net')) {
  root.textContent = 'Birbly has moved to birbly.app…';
  const { pathname, search, hash } = window.location;
  window.location.replace(new URL(pathname + search + hash, 'https://birbly.app').href);
  throw new Error('Moved to birbly.app.');
}

// In the app (apps/mobile), reopen on the side this device was last used
// for: a child's tablet comes back as their page. See native/platform.ts.
if (nativeShell() && window.location.pathname.replace(/\/+$/, '') === '' && appHome() === '/me') {
  window.history.replaceState({}, '', '/me' + window.location.hash);
}

// TWO APPS (2026-10-19). Birbly Kids is only ever the child's page (and the
// privacy notice); the parents' app never shows it. Whatever path a reload
// or a stray link lands on, each stays on its own side.
{
  const here = window.location.pathname.replace(/\/+$/, '');
  const page = here === '/privacy' || here === '/terms';
  if (appSide() === 'kids' && here !== '/me' && !page) window.history.replaceState({}, '', '/me');
  if (appSide() === 'parents' && here === '/me') window.history.replaceState({}, '', '/');
}

const path = window.location.pathname.replace(/\/+$/, '');
const child = path === '/me';
// The privacy policy and terms: plain pages, no sign-in, nothing loaded.
const legal = path === '/privacy' || path === '/terms';

// Read the fragment, stash the code, rewrite the address bar. Each does
// nothing except on its own path and neither throws. Called before anything
// renders, and after the child check has read the path -- `captureJoinCode`
// changes the path, so reading it first is not optional.
if (child) {
  rememberAppHome('/me');
  captureChildCode();
} else if (path === '/consent') {
  // The parental-consent email's link (family/consent.ts): held across a
  // sign-in redirect, confirmed on Home.
  takeConsentLink();
} else {
  captureJoinCode();
}

// The art preview, for whoever is changing the art. DEVELOPMENT ONLY: Vite
// replaces `import.meta.env.DEV` with `false` in a build, so this branch and
// the dynamic import inside it are dead code there and never shipped. See
// src/valley/Preview.tsx.
const preview =
  import.meta.env.DEV && window.location.pathname.replace(/\/+$/, '') === '/preview';
// The real parent app over a made-up family. DEVELOPMENT ONLY, and for the
// same reason: see src/dev/sampleFamily.ts.
const sample =
  import.meta.env.DEV && window.location.pathname.replace(/\/+$/, '') === '/preview/parent';

const sampleChild =
  import.meta.env.DEV && window.location.pathname.replace(/\/+$/, '') === '/preview/child';

if (sampleChild) {
  void import('./dev/sampleFamily')
    .then(async ({ startSampleChild }) => {
      await startSampleChild();
      createRoot(root).render(<StrictMode><ChildApp /></StrictMode>);
    })
    .catch((problem: unknown) => {
      root.textContent = `The sample child did not start: ${String(problem)}`;
    });
} else if (sample) {
  void import('./dev/sampleFamily').then(async ({ startSampleFamily }) => {
    await startSampleFamily();
    createRoot(root).render(<StrictMode><App /></StrictMode>);
  });
} else if (preview) {
  void import('./valley/Preview').then(({ Preview }) => {
    createRoot(root).render(<StrictMode><Preview /></StrictMode>);
  });
} else if (legal) {
  void import('./screens/Legal').then(({ Privacy, Terms }) => {
    createRoot(root).render(<StrictMode>{path === '/privacy' ? <Privacy /> : <Terms />}</StrictMode>);
  });
} else {
  createRoot(root).render(
    <StrictMode>{child ? <ChildApp /> : <App />}</StrictMode>,
  );
}
